import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    InputSignal,
    OnDestroy,
    OnInit,
    output,
    OutputEmitterRef,
    Signal,
    signal,
    WritableSignal
} from "@angular/core";
import {onEach} from '../../../utils/observable-utils';
import {
    CardPaymentSubmissionFailure,
    CardPaymentSubmissionResult,
    PaymentAuthorisationType,
    PaymentCard
} from '../../payment';
import {ResponsiveService} from "../../../responsive/responsive.service";
import {ColorPairData} from "../../../configuration/app-entity.model";
import {ThemeModel} from '../../../theme/theme.model';
import {Color} from "../../../ui-elements/color";
import {SpinnerComponent} from "../../../ui-elements/spinner/spinner.component";
import {ContentMessageComponent} from "../../../notification/message/content-message.component";
import {MessageLevel} from "../../../notification/message/message";
import {TyroPayRequest, TyroPayRequestError, TyroPayRequestErrorType, TyroPayRequestStatus} from "../../tyro";
import {PaymentModel} from "../../payment.model";
import {SelectorControlComponent} from '../../../inputs/selector-control/selector-control.component';
import {ConfigurationModel} from '../../../configuration/configuration.model';

@Component({
    selector: "woa-tyro-manual-payment-card-panel",
    templateUrl: "./tyro-manual-payment-card-panel.component.html",
    styleUrl: "./tyro-manual-payment-card-panel.component.scss",
    host: {
        "[class.disabled]": "disabled()"
    },
    providers: [{provide: PaymentCard, useExisting: TyroManualPaymentCardPanelComponent}],
    imports: [
        ContentMessageComponent,
        SelectorControlComponent,
        SpinnerComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TyroManualPaymentCardPanelComponent extends PaymentCard implements OnInit, AfterViewInit, OnDestroy {
    private destroyRef: DestroyRef = inject(DestroyRef);
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private responsiveService: ResponsiveService = inject(ResponsiveService);
    public paymentModel: PaymentModel = inject(PaymentModel);
    private themeModel: ThemeModel = inject(ThemeModel);

    MessageLevel = MessageLevel;

    public readonly disabled: InputSignal<boolean> = input<boolean>(false);
    public readonly hidden: InputSignal<boolean> = input<boolean>(false);

    public onPayFormReady: OutputEmitterRef<boolean> = output<boolean>();

    public readonly payFormError: WritableSignal<string> = signal<string>(undefined);
    public readonly submitted: WritableSignal<boolean> = signal<boolean>(false);
    private readonly payFormReady: WritableSignal<boolean> = signal<boolean>(false);

    public readonly showSpinner: Signal<boolean> = computed<boolean>(() =>
        !this.hidden() && !this.submitted() && (!this.paymentModel.paymentPlatformPublicKey() || (!this.payFormReady() && !this.payFormError())));

    public readonly showSavePaymentDetailsOption: Signal<boolean> = computed<boolean>(() =>
        this.configurationModel.isConsumerActor());

    public savePaymentDetails: boolean = false;
    private hasBeenUnhidden: boolean = false;
    private tyro: any;
    private payForm: any;
    private paymentPlatformPublicKey: string;
    private tyroPayFormHolder: Element;
    private destroyed: boolean = false;

    constructor() {
        super();
        this.savePaymentDetails = this.responsiveService.isMobile() || this.configurationModel.devOrDemoMode();

        effect(() => {
            if (!this.hidden()) {
                if (!this.hasBeenUnhidden) {
                    setTimeout(() => this.setupTyroPayForm());
                }
                this.hasBeenUnhidden = true;
            }
        });

        effect(() => {
            if (this.responsiveService.windowWidth() && this.payForm && this.payForm["resize"]) {
                this.payForm.resize();
            }
        });

        effect(() => this.enableOrDisableTyroPayForm());
    }

    ngOnInit(): void {
        onEach(this.paymentModel.paymentDetailsApplied$, async () => this.setupTyroPayForm(), this.destroyRef);
    }

    ngAfterViewInit(): void {
        void this.setupTyroPayForm();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
    }

    public canSavePaymentDetails(): boolean {
        return this.showSavePaymentDetailsOption() && this.savePaymentDetails;
    }

    public isValid(): boolean {
        this.submitted.set(true);
        return true;
    }

    public markAsUnsubmitted(): void {
        this.submitted.set(false);
    }

    async submit(): Promise<CardPaymentSubmissionResult | CardPaymentSubmissionFailure> {
        if (!this.payFormReady()) {
            return undefined;
        }
        try {
            let payRequest: TyroPayRequest = await this.tyro.fetchPayRequest();
            if (payRequest.status == TyroPayRequestStatus.SUCCESS) {
                return new CardPaymentSubmissionResult(PaymentAuthorisationType.TYRO_PAYMENT_INTENT);
            }
            if (payRequest.status !== TyroPayRequestStatus.AWAITING_PAYMENT_INPUT && payRequest.status !== TyroPayRequestStatus.FAILED) {
                // noinspection ExceptionCaughtLocallyJS
                throw "Unexpected pay request status: " + payRequest.status;
            }
            await this.tyro.submitPay();
            payRequest = await this.tyro.fetchPayRequest();
            if (payRequest.status !== TyroPayRequestStatus.SUCCESS) {
                // noinspection ExceptionCaughtLocallyJS
                throw "Unexpected pay request status: " + payRequest.status;
            }
            return new CardPaymentSubmissionResult(PaymentAuthorisationType.TYRO_PAYMENT_INTENT);
        } catch (error) {
            console.error(error);
            if (error.type) {
                const payRequestError: TyroPayRequestError = <TyroPayRequestError>error;
                if (payRequestError.type === TyroPayRequestErrorType.UNKNOWN_ERROR) {
                    throw error;
                }
                return new CardPaymentSubmissionFailure(payRequestError.errorMessage, payRequestError.errorCode, payRequestError.gatewayCode,
                    payRequestError.type === TyroPayRequestErrorType.CLIENT_VALIDATION_ERROR);
            }
            throw error;
        }
    }

    private async setupTyroPayForm(): Promise<void> {
        if (this.destroyed || !this.hasBeenUnhidden) {
            return;
        }

        this.enableOrDisableTyroPayForm();

        // Tyro uses the following test to check that it can inject its form - we can't use a ViewChild, because it needs to be in the HTML DOM.
        this.tyroPayFormHolder = document.querySelector("#tyro-card-pay-form-holder");
        if (!this.tyroPayFormHolder) {
            setTimeout(async () => await this.setupTyroPayForm(), 500);
            return;
        }

        // The tyro secret (paymentPlatformPublicKey) changes if the order is re-initiated - the tyro form needs to be rebuilt.
        if (!this.paymentModel.paymentPlatformPublicKey() || this.paymentPlatformPublicKey === this.paymentModel.paymentPlatformPublicKey()) {
            return;
        }
        this.paymentPlatformPublicKey = this.paymentModel.paymentPlatformPublicKey();

        try {
            console.log("Initialising Tyro for card payment. Live Mode = ", this.paymentModel.paymentPlatformLiveMode());
            this.tyro = (<any>window).Tyro({
                liveMode: this.paymentModel.paymentPlatformLiveMode()
            });
            await this.tyro.init(this.paymentPlatformPublicKey);

            const standardPage: ColorPairData = this.themeModel.getPaletteEntry("standard-page");
            const brand: ColorPairData = this.themeModel.getPaletteEntry("brand");
            const danger: ColorPairData = this.themeModel.getPaletteEntry("danger");

            this.payForm = this.tyro.createPayForm({
                options: {
                    creditCardForm: {
                        enabled: true,
                        supportedNetworks: ["visa", "mastercard", "amex"]
                    }
                },
                styleProps: {
                    bodyPadding: "0",
                    showSupportedCards: false,
                    fontFamily: "Inter, sans-serif",
                    labelPosition: "block",
                    labelPadding: "0 0 7px",
                    labelFontSize: "14px",
                    labelFontWeight: "700",
                    labelFontColor: standardPage.foregroundColor,
                    inputBackgroundColor: standardPage.backgroundColor,
                    inputBorderColor: Color.fromString(brand.backgroundColor).applyAlpha(Color.fromString(standardPage.backgroundColor), 0.3).toString(),
                    inputBorderSize: "1px",
                    inputBorderRadius: "3px",
                    inputFontSize: "16px",
                    inputFontWeight: "400",
                    inputFontColor: standardPage.foregroundColor,
                    inputErrorBorderColor: Color.fromString(brand.backgroundColor).applyAlpha(Color.fromString(standardPage.backgroundColor), 0.3).toString(),
                    inputErrorBorderSize: "1px",
                    inputErrorFontColor: standardPage.foregroundColor,
                    inputFocusBorderColor: Color.fromString(brand.backgroundColor).applyAlpha(Color.fromString(standardPage.backgroundColor), 0.5).toString(),
                    inputFocusBorderSize: "1px",
                    inputFocusFontColor: standardPage.foregroundColor,
                    errorFontSize: "14px",
                    errorFontWeight: "600",
                    errorFontColor: danger.backgroundColor
                }
            });
            if ((<HTMLElement>this.tyroPayFormHolder).firstChild) {
                this.tyroPayFormHolder.removeChild((<HTMLElement>this.tyroPayFormHolder).firstChild);
            }
            await this.payForm.inject("#tyro-card-pay-form-holder");
            this.payForm.setReadyListener(() => {
                this.enableOrDisableTyroPayForm();
                this.payFormReady.set(true);
                this.onPayFormReady.emit(true);
            });
        } catch (error) {
            console.error("Adyen Tyro pay form error", error);
            this.payFormError.set(error.message.replace(/\.$/, ""));
            setTimeout(() => this.onPayFormReady.emit(false), 300);
        }
    }

    private enableOrDisableTyroPayForm(): void {
        if (this.tyroPayFormHolder && (<HTMLElement>this.tyroPayFormHolder).firstChild) {
            (<HTMLElement>(<HTMLElement>this.tyroPayFormHolder).firstChild).tabIndex = this.disabled() ? -1 : undefined;
        }
    }
}
